import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { User } from '../_models/user';
import { Globals } from '../_globals/globals';


@Injectable({
  providedIn: 'root'
})

export class AuthenticationService {
  public currentUser: Observable<User>;
  private currentUserSubject: BehaviorSubject<User>;
  private sessionExpiryTimer: any;
  private sessionRedirectInProgress = false;

  constructor(
    private http: HttpClient,
    private globals: Globals,
    private router: Router
  ) {
    const hasStoredSession = localStorage.getItem('currentUser') !== null;
    const storedUser = this.readStoredUser();
    const currentUser = storedUser && this.isSessionValid(storedUser) ? storedUser : null;

    if (hasStoredSession && !currentUser) {
      this.clearStoredUser();
    }

    this.currentUserSubject = new BehaviorSubject<User>(currentUser);
    this.currentUser = this.currentUserSubject.asObservable();
    if (hasStoredSession && !currentUser) {
      setTimeout(() => this.redirectToLoginForExpiredSession(), 0);
    } else {
      this.scheduleSessionExpiry(currentUser);
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  // Función que llama a la función del controlador (mediante un POST) que comprueba y loguea al usuario.
  login(email: string, password: string) {
    return this.http.post<User>(this.globals.apiUrl + '/auth/login', {email, password})
      .pipe(map(user => {

        console.log('-> AuthenticationService -> Usuario logueado desde login:');
        // console.log(user);

        // login successful if there's a jwt token in the response
        if (user && user.token) {

          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem('currentUser', JSON.stringify(user));
          localStorage.setItem('user_id', user.user_id.toString());
          localStorage.setItem('role', user.role);
          localStorage.setItem('lang', user.lang);
          localStorage.setItem('company', user.company);
          localStorage.setItem('company_email', user.company_email);
          localStorage.setItem('company_phone', user.company_phone);
          localStorage.setItem('token', user.token);
          localStorage.setItem('name', user.name);
          localStorage.setItem('email', user.email);
          localStorage.setItem('phone', user.phone);
          localStorage.setItem('enviroment', user.enviroment);
          localStorage.setItem('permissions', user.permissions);
          localStorage.setItem('group_id', user.group_id.toString());
          this.currentUserSubject.next(user);
          this.sessionRedirectInProgress = false;
          this.scheduleSessionExpiry(user);
        }
        return user;
      }));
  }

  logout() {
    // remove user from local storage to log user out
    this.clearSessionExpiryTimer();
    this.clearStoredUser();
    this.currentUserSubject.next(null);
  }

  public isSessionValid(userIn?: User): boolean {
    const user = userIn || this.currentUserValue;
    if (!user || !user.token) {
      return false;
    }

    const expirationTime = this.getTokenExpirationTime(user.token);
    return expirationTime !== null && expirationTime > Date.now();
  }

  public redirectToLoginForExpiredSession(): void {
    if (this.sessionRedirectInProgress) {
      return;
    }

    const currentUrl = this.router.url || '';
    const alreadyOnLogin = currentUrl.split('?')[0] === '/login';
    this.sessionRedirectInProgress = true;
    this.logout();

    if (alreadyOnLogin) {
      this.sessionRedirectInProgress = false;
      return;
    }

    const queryParams: any = { reason: 'session_expired' };
    if (currentUrl && currentUrl !== '/' && currentUrl.indexOf('/login') !== 0) {
      queryParams.returnUrl = currentUrl;
    }

    this.router.navigate(['/login'], {
      queryParams: queryParams,
      replaceUrl: true
    }).then(() => {
      this.sessionRedirectInProgress = false;
    }).catch(() => {
      this.sessionRedirectInProgress = false;
    });
  }

  private readStoredUser(): User {
    const storedUser = localStorage.getItem('currentUser');
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser) as User;
    } catch (error) {
      return null;
    }
  }

  private clearStoredUser(): void {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('user_id');
    localStorage.removeItem('role');
    localStorage.removeItem('lang');
    localStorage.removeItem('company');
    localStorage.removeItem('company_email');
    localStorage.removeItem('company_phone');
    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('phone');
    localStorage.removeItem('enviroment');
    localStorage.removeItem('permissions');
    localStorage.removeItem('group_id');
  }

  private getTokenExpirationTime(tokenIn: string): number {
    try {
      const tokenParts = tokenIn.split('.');
      if (tokenParts.length !== 3) {
        return null;
      }

      let encodedPayload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (encodedPayload.length % 4) {
        encodedPayload += '=';
      }

      const decodedPayload = window.atob(encodedPayload);
      const utf8Payload = decodeURIComponent(decodedPayload.split('').map(character => {
        return '%' + ('00' + character.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const payload = JSON.parse(utf8Payload);
      const expiration = Number(payload.exp);

      return isFinite(expiration) ? expiration * 1000 : null;
    } catch (error) {
      return null;
    }
  }

  private scheduleSessionExpiry(userIn: User): void {
    this.clearSessionExpiryTimer();
    if (!userIn || !userIn.token) {
      return;
    }

    const expirationTime = this.getTokenExpirationTime(userIn.token);
    if (expirationTime === null) {
      this.sessionExpiryTimer = setTimeout(() => this.redirectToLoginForExpiredSession(), 0);
      return;
    }

    const remainingTime = expirationTime - Date.now();
    if (remainingTime <= 0) {
      this.sessionExpiryTimer = setTimeout(() => this.redirectToLoginForExpiredSession(), 0);
      return;
    }

    // Los navegadores limitan setTimeout a unos 24,8 días. Si en el futuro
    // aumenta la duración del JWT, se vuelve a calcular al alcanzar el límite.
    const maximumTimeout = 2147483647;
    this.sessionExpiryTimer = setTimeout(() => {
      if (this.isSessionValid(userIn)) {
        this.scheduleSessionExpiry(userIn);
        return;
      }
      this.redirectToLoginForExpiredSession();
    }, Math.min(remainingTime, maximumTimeout));
  }

  private clearSessionExpiryTimer(): void {
    if (this.sessionExpiryTimer) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = null;
    }
  }

  redirectToLoginForAccessBlock(messageIn?: string): void {
    sessionStorage.setItem('dimpro_access_block_message', messageIn || 'Tu acceso ha sido bloqueado. Contacta con soporte.');
    this.logout();
    window.location.assign('/login?reason=access_blocked');
  }

  refreshToken(): Observable<User> {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const token = currentUser.refreshToken;

    console.log('-->->* AuthenticationService: Refrescando token...');

    // return this.http.post<User>("http://localhost:53217/api/Account/Token/Refresh", { 'token': token })
    return this.http.post<User>(this.globals.apiUrl + '/auth/refresh', { token })
      .pipe(
        map(user => {
          if (user && user.token) {

            console.log('refreshToken --> Usuario autenticado:');
            console.log(user);

            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            localStorage.setItem('user_id', user.user_id.toString());
            localStorage.setItem('role', user.role);
            localStorage.setItem('lang', user.lang);
            localStorage.setItem('company', user.company);
            localStorage.setItem('token', user.token);
            localStorage.setItem('name', user.name);
            localStorage.setItem('email', user.email);
            localStorage.setItem('enviroment', user.enviroment);
            localStorage.setItem('permissions', user.permissions);
            localStorage.setItem('group_id', user.group_id.toString());
            this.currentUserSubject.next(user);
            this.scheduleSessionExpiry(user);
          } else {
            console.log('refreshToken -> *** Usuario NO autenticado:');
            console.log(user);
          }
          return user as User;
      }));
  }
}
